import type { FieldSpec } from "@lift-admin/utils/logChangeFormatter";

/** Fields included in brand admin log diffs / details. */
export const BRAND_FIELD_SPECS: FieldSpec[] = [
  { key: "name", label: "Name" },
  { key: "organization", label: "Organization" },
  { key: "domain", label: "Domain" },
  { key: "type", label: "Type" },
  { key: "vertical", label: "Vertical" },
  { key: "timezone", label: "Timezone" },
  { key: "week_start_day", label: "Week Start Day" },
  { key: "date_preset", label: "Date Preset" },
  { key: "launch", label: "Launch Date", type: "date" },
  { key: "date_joined", label: "Date Joined", type: "date" },
  { key: "paid_alias", label: "Paid Alias" },
  { key: "affiliate_alias", label: "Affiliate Alias" },
  { key: "hubspot_company_id", label: "HubSpot Company ID" },
  {
    key: "is_performance_creator",
    label: "Performance Creator",
    type: "boolean",
    boolStyle: "yesNo",
  },
  { key: "sow_link", label: "SOW Link" },
  { key: "msa_link", label: "MSA Link" },
  { key: "contract_terms", label: "Contract Terms" },
  { key: "director_id", label: "Director ID" },
  { key: "manager_id", label: "Manager ID" },
  { key: "associate_1_id", label: "Associate 1 ID" },
  { key: "associate_2_id", label: "Associate 2 ID" },
  { key: "ops_id", label: "Ops ID" },
  { key: "revenue_source_of_truth", label: "Revenue Source of Truth" },
  { key: "conversion_source_of_truth", label: "Conversion Source of Truth" },
  {
    key: "is_active",
    label: "Status",
    type: "boolean",
    boolStyle: "activeInactive",
  },
  { key: "products", label: "Products", type: "list" },
];

/** Subset used for create/delete detail rows (skip empty). */
export const BRAND_CREATE_FIELD_SPECS: FieldSpec[] = [
  { key: "name", label: "Name" },
  { key: "organization", label: "Organization" },
  { key: "type", label: "Type" },
  { key: "domain", label: "Domain" },
  { key: "vertical", label: "Vertical" },
  { key: "timezone", label: "Timezone" },
  { key: "week_start_day", label: "Week Start Day" },
  { key: "date_preset", label: "Date Preset" },
  { key: "launch", label: "Launch Date", type: "date" },
  { key: "date_joined", label: "Date Joined", type: "date" },
  { key: "paid_alias", label: "Paid Alias" },
  { key: "affiliate_alias", label: "Affiliate Alias" },
  { key: "hubspot_company_id", label: "HubSpot Company ID" },
  {
    key: "is_performance_creator",
    label: "Performance Creator",
    type: "boolean",
    boolStyle: "yesNo",
  },
  { key: "products", label: "Products", type: "list" },
  {
    key: "is_active",
    label: "Status",
    type: "boolean",
    boolStyle: "activeInactive",
  },
];

const toBool = (value: any): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "yes" || v === "true" || v === "1";
  }
  return Boolean(value);
};

const normalizeVertical = (value: any): string =>
  (Array.isArray(value) ? value : String(value ?? "").split(","))
    .map((s: string) => s.trim())
    .filter(Boolean)
    .join(", ");

const toComparableDate = (value: any): string => {
  if (value == null || value === "") return "";
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return date.toISOString();
  } catch {
    return String(value);
  }
};

/** UI label for the legacy `is_acorn` / "Acorn" product flag. */
export const ACORN_BRAND_FEATURE_LABEL = "Influencer (previously Acorn)";

/** Brand feature option keys whose display name differs from the key. */
const PRODUCT_LABELS: Record<string, string> = {
  Acorn: ACORN_BRAND_FEATURE_LABEL,
};

/** Display name for a brand feature option key (e.g. "Acorn" -> Influencer label). */
export const productLabel = (option: string): string =>
  PRODUCT_LABELS[option] ?? option;

const toProducts = (item: any): string[] => {
  if (Array.isArray(item?.products)) {
    return item.products.map((p: any) => productLabel(String(p))).filter(Boolean);
  }
  return [
    item?.is_affiliate && "Affiliate",
    item?.is_paid && "Performance",
    item?.is_creative && "Creative",
    item?.is_acorn && productLabel("Acorn"),
  ].filter(Boolean) as string[];
};

/**
 * Normalize brand entity / form shape into comparable log fields.
 * Pass `organization` as a resolved display name when available.
 */
export function normalizeBrandForLog(item: any = {}): Record<string, any> {
  if (!item) return {};
  return {
    name: item.name ?? "",
    organization:
      item.organization ??
      item.organization_name ??
      (item.organization_id != null ? `ID ${item.organization_id}` : ""),
    domain: item.domain ?? "",
    type: item.type ?? "",
    vertical: normalizeVertical(item.vertical),
    timezone: item.timezone ?? "",
    week_start_day: item.week_start_day ?? "",
    date_preset: item.date_preset ?? "",
    launch: toComparableDate(item.launch),
    date_joined: toComparableDate(item.date_joined),
    paid_alias: item.paid_alias ?? item.alias ?? "",
    affiliate_alias: item.affiliate_alias ?? "",
    hubspot_company_id:
      item.hubspot_company_id ?? item.hubspotCompanyId ?? "",
    is_performance_creator: toBool(
      item.is_performance_creator ?? item.is_influencer,
    ),
    sow_link: item.sow_link ?? item.sowLink ?? "",
    msa_link: item.msa_link ?? item.msaLink ?? "",
    contract_terms: item.contract_terms ?? item.contractTerms ?? "",
    director_id: item.director_id ?? item.directorId ?? "",
    manager_id: item.manager_id ?? item.managerId ?? "",
    associate_1_id: item.associate_1_id ?? item.associateId1 ?? "",
    associate_2_id: item.associate_2_id ?? item.associateId2 ?? "",
    ops_id: item.ops_id ?? item.opsId ?? "",
    revenue_source_of_truth:
      item.revenue_source_of_truth ??
      item.brandData?.revenue_source_of_truth ??
      "",
    conversion_source_of_truth:
      item.conversion_source_of_truth ??
      item.brandData?.conversion_source_of_truth ??
      "",
    is_active: item.is_active != null ? toBool(item.is_active) : undefined,
    products: toProducts(item),
  };
}
